import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import CollapsibleSideNavigation from "./side-navigation";
import MobileBottomNav from "./mobile/mobile-bottom-nav";
import MobileMoreSheet from "./mobile/mobile-more-sheet";
import { MobileNavigationContext } from "./mobile/mobile-navigation-context";
import { useIsMobile } from "../hooks/use-is-mobile";

/** Full-screen task flows with their own bottom action bar. */
const ROUTES_WITHOUT_TAB_BAR = ["/add-activity", "/create-quotation", "/create-invoice"];

const DesktopLayout: React.FC = () => (
  <div className="App flex h-screen overflow-hidden">
    <CollapsibleSideNavigation />
    <main className="flex-1 overflow-auto w-0 min-w-0">
      <Outlet />
    </main>
  </div>
);

const MobileLayout: React.FC = () => {
  const { pathname } = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const showTabBar = !ROUTES_WITHOUT_TAB_BAR.includes(pathname);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [pathname]);

  const openMenu = useCallback(() => setIsMenuOpen(true), []);
  const closeMenu = useCallback(() => setIsMenuOpen(false), []);
  const contextValue = useMemo(
    () => ({ openMenu, hasTabBar: showTabBar }),
    [openMenu, showTabBar],
  );

  return (
    <MobileNavigationContext.Provider value={contextValue}>
      <div className="App relative h-dvh overflow-hidden bg-gray-50">
        <main
          className="h-full overflow-hidden"
          style={
            showTabBar
              ? { paddingBottom: "calc(4rem + env(safe-area-inset-bottom))" }
              : undefined
          }
        >
          <Outlet />
        </main>
        {showTabBar && <MobileBottomNav isMenuOpen={isMenuOpen} onOpenMenu={openMenu} />}
        <MobileMoreSheet open={isMenuOpen} onClose={closeMenu} />
      </div>
    </MobileNavigationContext.Provider>
  );
};

const AppLayout: React.FC = () => {
  const isMobile = useIsMobile();
  return isMobile ? <MobileLayout /> : <DesktopLayout />;
};

export default AppLayout;
